import { t } from "@hakili/i18n";
import { useEffect, useState } from "react";

import type { Vue } from "../shell/views.js";

import { accountLine, ManagerBadge } from "./partner-cards.js";
import type { PartnerType } from "./priorites.js";

/**
 * `round CN-C`, `CN-C.2`, WF-CON-39: **« Développement », to the drawing
 * (`hakili-console.jsx`, `DeveloppementScreen`).**
 *
 * The stage is a fact of `partnerships` on the account (`pipeline_stage`),
 * read from `GET /partners/pipeline` and moved by `POST
 * /partners/pipeline/{accountId}/advance`, journalled there. An active
 * partner appears from its own source and is never a prospect twice. The
 * composer writes to the journal door as the fiche's does. The drawing's
 * « contact », « potentiel » and « prochaine action » are a prospect's own
 * fields no door holds: dashes (`R81`).
 */

export const PIPELINE_STAGES = [
  "prospect",
  "premierContact",
  "ndaSigne",
  "accordPilote",
  "parametrage",
  "actif",
] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export interface PipelineLineView {
  readonly accountId: string;
  readonly organisationId: string | null;
  readonly name: string;
  readonly type: PartnerType;
  readonly stage: PipelineStage;
  readonly lastExchangeDays: number | null;
  readonly lastExchangeBody: string | null;
  /**
   * `FX-3`, `M1`: `WF-CON-35`'s own guard , *« a relationship view only sees ITS partners; the admin sees
   * everything with the manager named on each item »*. The badge on this list was `name={null}`, a
   * literal, so every row of the screen where `S2-9` was SEEN read « Gestionnaire non renseigné ».
   */
  readonly managerName?: string | null;
  /**
   * `FX-3`, `M4 (a)` (`S2-7`, `S2-8`): the SAME facts the partner card's account line is derived from, so
   * this screen and that one cannot disagree about what « active » means on one partner. Optional, because
   * an older answer without them leaves the line's dash where the sentence goes.
   */
  readonly organisationStatus?: string | null;
  readonly activeCount?: number;
  readonly invitationPendingSince?: string | null;
  readonly administratorName?: string | null;
}

export interface PipelineView {
  readonly types: readonly PartnerType[];
  readonly counters: readonly { readonly stage: PipelineStage; readonly count: number }[];
  readonly lines: readonly PipelineLineView[];
}

export interface DeveloppementDoors {
  readonly readPipeline: () => Promise<PipelineView>;
  readonly advance: (accountId: string) => Promise<void>;
  readonly logExchange: (
    organisationId: string,
    input: { readonly type: string; readonly body: string },
  ) => Promise<void>;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

/** The wire's stage codes, mapped to the record's words (`09-01`). */
const STAGE_KEY = new Map<PipelineStage, Parameters<typeof t>[0]>([
  ["prospect", "console.developpement.stadeProspect"],
  ["premierContact", "console.developpement.stadePremierContact"],
  ["ndaSigne", "console.developpement.stadeNdaSigne"],
  ["accordPilote", "console.developpement.stadeAccordPilote"],
  ["parametrage", "console.developpement.stadeParametrage"],
  ["actif", "console.developpement.stadeActif"],
]);

const stageWord = (stage: PipelineStage): string =>
  t(STAGE_KEY.get(stage) ?? "console.developpement.stadeProspect");

const SUBTITLE = new Map<Vue, Parameters<typeof t>[0]>([
  ["banques", "console.developpement.sousTitreBanques"],
  ["concess", "console.developpement.sousTitreConcess"],
]);

const CHANNELS = ["appel", "visite", "courriel", "comite", "whatsapp"] as const;

export interface DeveloppementProps {
  readonly vue: Vue;
  readonly pipeline: PipelineView | null;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onAdvance: (accountId: string) => void;
  readonly onExchange: (organisationId: string, type: string, body: string) => Promise<boolean>;
}

export function Developpement(props: DeveloppementProps): React.JSX.Element {
  const [filter, setFilter] = useState<"tous" | "banque" | "concessionnaire">("tous");
  const [open, setOpen] = useState<string | null>(null);
  const [channel, setChannel] = useState<string>("appel");
  const [body, setBody] = useState("");
  const admin = props.vue === "admin";
  const lines = (props.pipeline?.lines ?? []).filter((one) => filter === "tous" || one.type === filter);
  const counters = PIPELINE_STAGES.map((stage) => ({
    stage,
    count: lines.filter((one) => one.stage === stage).length,
  }));

  return (
    <div data-testid="developpement">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("console.developpement.titre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">
          {t(SUBTITLE.get(props.vue) ?? "console.developpement.sousTitreAdmin")}
        </p>
      </div>
      {admin ? (
        <div className="mb-4 flex items-center gap-2">
          {(
            [
              ["tous", "console.priorites.filtreTous"],
              ["banque", "console.priorites.filtreBanques"],
              ["concessionnaire", "console.priorites.filtreConcess"],
            ] as const
          ).map(([id, key]) => (
            <button
              key={id}
              type="button"
              data-testid={`developpement-filtre-${id}`}
              onClick={() => {
                setFilter(id);
              }}
              className={
                "rounded-full border px-3 py-1.5 text-xs font-medium " +
                (filter === id
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
              }
            >
              {t(key)}
            </button>
          ))}
        </div>
      ) : null}

      <div data-testid="developpement-compteurs" className="mb-6 grid grid-cols-6 gap-3">
        {counters.map((one) => (
          <div
            key={one.stage}
            data-testid={`compteur-${one.stage}`}
            className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
          >
            <p className="text-xs leading-snug text-gray-500">{stageWord(one.stage)}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{one.count}</p>
          </div>
        ))}
      </div>

      {props.refusal === null ? null : (
        <p role="alert" data-testid="developpement-refus" className="mb-3 text-sm text-rose-600">
          {props.refusal}
        </p>
      )}

      <div className="mb-4 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-2.5">
          <span className="w-56 text-xs text-gray-500">{t("console.developpement.colCompte")}</span>
          <span className="flex-1 text-xs text-gray-500">{t("console.developpement.colPotentiel")}</span>
          <span className="w-32 text-right text-xs text-gray-500">
            {t("console.developpement.colDernier")}
          </span>
          <span className="w-32 text-center text-xs text-gray-500">
            {t("console.developpement.colStade")}
          </span>
          <span className="w-52 text-right text-xs text-gray-500">
            {t("console.developpement.colAvancer")}
          </span>
        </div>
        {props.pipeline === null ? (
          <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
        ) : lines.length === 0 ? (
          <p data-testid="developpement-vide" className="px-5 py-5 text-sm text-gray-500">
            {t("console.developpement.vide")}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {lines.map((line) => {
              const active = line.stage === "actif";
              return (
                <li key={line.accountId} data-testid={`pipeline-${line.accountId}`} className="px-5 py-3.5">
                  <div className="flex items-center gap-4">
                    <div className="w-56 shrink-0">
                      <p className={"text-sm font-medium " + (active ? "text-gray-500" : "text-gray-900")}>
                        {line.name}
                      </p>
                      {/* `FX-3`, `M4 (a)`: the account's state, by the CARD's own derivation , one
                          function, two screens. « Actif » in this column is the prospection funnel's last
                          stage (the partner is onboarded); whether anybody can SIGN IN is a different fact,
                          and the sitting found the two of them disagreeing on one partner. */}
                      <p
                        data-testid={`pipeline-${line.accountId}-comptes`}
                        className="truncate text-sm text-gray-500"
                      >
                        {accountLine({
                          id: line.organisationId ?? line.accountId,
                          name: line.name,
                          type: line.type,
                          administrator:
                            line.administratorName === null || line.administratorName === undefined
                              ? null
                              : {
                                  fullName: line.administratorName,
                                  email: "",
                                  state: "active",
                                  invitationId: null,
                                },
                          ...(line.organisationStatus === null || line.organisationStatus === undefined
                            ? {}
                            : { status: line.organisationStatus }),
                          ...(line.activeCount === undefined ? {} : { activeCount: line.activeCount }),
                          invitationPendingSince: line.invitationPendingSince ?? null,
                        })}
                      </p>
                      {admin ? <ManagerBadge name={line.managerName} /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-700">{DASH}</p>
                      <p className="truncate text-xs text-gray-500">
                        {line.organisationId === null
                          ? DASH
                          : `${t("console.developpement.dejaAuPortefeuille")} · ${line.lastExchangeBody ?? DASH}`}
                      </p>
                    </div>
                    <span
                      data-testid="pipeline-dernier"
                      className={
                        "w-32 shrink-0 text-right text-sm tabular-nums " +
                        (line.lastExchangeDays !== null && line.lastExchangeDays > 21
                          ? "font-medium text-amber-700"
                          : "text-gray-600")
                      }
                    >
                      {line.lastExchangeDays === null
                        ? DASH
                        : fill("console.developpement.ilYA", { n: String(line.lastExchangeDays) })}
                    </span>
                    <span className="w-32 shrink-0 text-center">
                      <span
                        data-testid="pipeline-stade"
                        className={
                          "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                          (active ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700")
                        }
                      >
                        {stageWord(line.stage)}
                      </span>
                    </span>
                    <span className="flex w-52 shrink-0 items-center justify-end gap-2">
                      {active ? (
                        <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          {t("console.developpement.auPortefeuille")}
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid="pipeline-avancer"
                          disabled={props.busy}
                          onClick={() => {
                            props.onAdvance(line.accountId);
                          }}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                        >
                          {t("console.developpement.faireAvancer")}
                        </button>
                      )}
                      {line.organisationId === null ? null : (
                        <button
                          type="button"
                          data-testid="pipeline-echange"
                          onClick={() => {
                            setOpen(open === line.accountId ? null : line.accountId);
                          }}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {open === line.accountId
                            ? t("console.developpement.masquer")
                            : t("console.developpement.ajouterEchange")}
                        </button>
                      )}
                    </span>
                  </div>
                  {open === line.accountId && line.organisationId !== null ? (
                    <form
                      data-testid="pipeline-composer"
                      className="mt-3 flex flex-wrap items-end gap-2 rounded-xl bg-gray-50 px-4 py-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const organisationId = line.organisationId;
                        if (organisationId === null) return;
                        void props.onExchange(organisationId, channel, body.trim()).then((accepted) => {
                          if (accepted) setBody("");
                        });
                      }}
                    >
                      <select
                        data-testid="pipeline-canal"
                        value={channel}
                        onChange={(event) => {
                          setChannel(event.target.value);
                        }}
                        className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                      >
                        {CHANNELS.map((one) => (
                          <option key={one} value={one}>
                            {one}
                          </option>
                        ))}
                      </select>
                      <input
                        data-testid="pipeline-texte"
                        value={body}
                        onChange={(event) => {
                          setBody(event.target.value);
                        }}
                        placeholder={t("console.partenaires.echangePlaceholder")}
                        className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs"
                      />
                      <button
                        type="submit"
                        data-testid="pipeline-enregistrer"
                        disabled={props.busy || body.trim() === ""}
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                      >
                        {t("console.partenaires.enregistrer")}
                      </button>
                    </form>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="text-sm text-gray-500">{t("console.developpement.prospectsRond")}</p>
    </div>
  );
}

export interface DeveloppementScreenProps extends DeveloppementDoors {
  readonly vue: Vue;
}

export function DeveloppementScreen(props: DeveloppementScreenProps): React.JSX.Element {
  const [pipeline, setPipeline] = useState<PipelineView | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readPipeline } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readPipeline();
        if (run.live) setPipeline(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readPipeline, epoch]);

  const act = async (gesture: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setRefusal(null);
    try {
      await gesture();
      setEpoch((now) => now + 1);
      return true;
    } catch (failure) {
      setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Developpement
      vue={props.vue}
      pipeline={pipeline}
      busy={busy}
      refusal={refusal}
      onAdvance={(accountId) => {
        void act(() => props.advance(accountId));
      }}
      onExchange={(organisationId, type, body) =>
        act(() => props.logExchange(organisationId, { type, body }))
      }
    />
  );
}
